import { ColumnDef } from '@tanstack/react-table'
import { Settings } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Doctor } from '@magnetar/data-access'
import { doctorService } from '@/services/doctor_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { DoctorDialog } from './DoctorDialog'
import { DeleteAlert } from '@/components/DeleteAlert'

export const columns: ColumnDef<Doctor>[] = [
  {
    accessorKey: 'doctor_id',
    header: 'ID',
  },
  {
    accessorKey: 'name',
    header: '姓名',
  },
  {
    accessorKey: 'specialty',
    header: '專科',
    cell: ({ row }) => {
      const specialties = row.original.specialty || []
      return (
        <div className="flex flex-wrap gap-1">
          {specialties.map((specialty, index) => (
            <Badge key={index} variant="outline">
              {specialty}
            </Badge>
          ))}
        </div>
      )
    },
  },
  {
    accessorKey: 'experience',
    header: '經歷',
    cell: ({ row }) => {
      const experiences = row.original.experience || []
      if (experiences.length === 0) return <span className="text-muted-foreground">無</span>

      return (
        <div className="max-w-[200px] truncate">
          {experiences[0]}
          {experiences.length > 1 && (
            <span className="text-muted-foreground"> 等{experiences.length}項</span>
          )}
        </div>
      )
    },
  },
  {
    id: 'actions',
    cell: function ActionsCell({ row, table }) {
      const doctor = row.original
      const { token } = useAuth()
      const [showEditDialog, setShowEditDialog] = useState(false)
      const [showDeleteAlert, setShowDeleteAlert] = useState(false)

      const handleDelete = async () => {
        if (!token) return

        const result = await doctorService.deleteDoctor(doctor.doctor_id, token)

        if (result.success) {
          toast.success('刪除醫生成功')
          table.options.meta?.reloadData?.()
        } else {
          toast.error('刪除醫生失敗')
        }
      }

      return (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>操作</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowEditDialog(true)}>編輯</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowDeleteAlert(true)} className="text-destructive">
                刪除
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* 編輯醫生對話框 */}
          <DoctorDialog
            doctor={doctor}
            open={showEditDialog}
            onOpenChange={setShowEditDialog}
            clinicId={doctor.clinic_id}
            onSuccess={() => table.options.meta?.reloadData?.()}
          />

          {/* 刪除確認對話框 */}
          <DeleteAlert
            open={showDeleteAlert}
            onOpenChange={setShowDeleteAlert}
            onConfirm={handleDelete}
            title="確定要刪除此醫生資料嗎？"
            description="此操作無法復原。這將永久刪除此醫生的設定和所有相關資料。"
          />
        </>
      )
    },
  },
]
